import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot, collection, query, where, getDocs, getDoc, documentId } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { FirebaseService } from '../../../shared/storage/FirebaseService';
import { useAuth } from '../../auth/contexts/AuthContext';
import { Squad, User } from '../../../types';
import HeaderBar from './HeaderBar';
import DashboardPane from './DashboardPane';
import ClassroomDashboard from './classroom/ClassroomDashboard';
import DashboardWallpaper from './classroom/DashboardWallpaper';
import RewardModal from './RewardModal';
import RewardConfirmationModal from './RewardConfirmationModal';
import TeacherToolbelt from './TeacherToolbelt';
import SquadGameAccessModal from './SquadGameAccessModal';
import SquadBoardModeModal from './SquadBoardModeModal';
import DraggableTimer from '../../../shared/ui/DraggableTimer';
import DraggableMusic from '../../../shared/ui/DraggableMusic';
import BonusActivitiesModal from './BonusActivitiesModal';
import '../styles/SquadDashboard.css';
import { SeasonPassService } from '../../progress/services/SeasonPassService';
import { getSeasonNodesByCurriculum } from '../../progress/data/seasonPassRegistry';
import { DEFAULT_SEASON } from '../../progress/data/seasonPassData';
import { getSquadCurriculumId, usesLegacyMusicDashboard } from '../utils/curriculum';
import { LOCATION_TEACHING_LOGISTICS_FIELDS, hasIncompleteTeacherLogistics } from '../utils/teacherLogisticsCompleteness';
import LoadingSpinner from '../../../shared/ui/LoadingSpinner';
import { useTeacherTools } from '../../../shared/contexts/TeacherToolsContext';
import AddStudentModal from './AddStudentModal';
import { canAccessSquad } from '../utils/squadAccess';

const SquadDashboard: React.FC = () => {
  const { squadId } = useParams<{ squadId: string }>();
  const [searchParams] = useSearchParams();
  const { currentUser, userData } = useAuth();
  const { setTools, clearTools } = useTeacherTools();
  const navigate = useNavigate();
  const [squad, setSquad] = useState<Squad | null>(null);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [locationLogistics, setLocationLogistics] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [isRewardConfirmationOpen, setIsRewardConfirmationOpen] = useState(false);
  const [students, setStudents] = useState<User[]>([]);
  const [isAwardingReward, setIsAwardingReward] = useState(false);
  const [pendingReward, setPendingReward] = useState<{
    students: { id: string; name: string; avatar: string; currentMusecash: number }[];
    amount: number;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<'roster' | 'curriculum' | 'calendar'>('roster');
  const initialTabParam = (searchParams.get('tab') || '').toLowerCase();
  const shouldOpenAttendance = searchParams.get('attendance') === '1';
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState<string | null>(null);
  const [isTimerOpen, setIsTimerOpen] = useState(false);
  const [isGameAccessOpen, setIsGameAccessOpen] = useState(false);
  const [isMusicOpen, setIsMusicOpen] = useState(false);
  const [isBoardModeOpen, setIsBoardModeOpen] = useState(false);
  const [isBonusActivitiesOpen, setIsBonusActivitiesOpen] = useState(false);
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);

  useEffect(() => {
    if (!currentUser || !squadId) {
      setError("Squad not found or user not logged in.");
      setLoading(false);
      return;
    }

    setLoading(true);

    const squadRef = doc(db, 'squads', squadId);

    const unsubscribe = onSnapshot(squadRef, (docSnap) => {
      if (docSnap.exists()) {
        const squadData = { ...docSnap.data(), id: docSnap.id } as Squad;
        if (!canAccessSquad(userData, squadData)) {
          setError("You don't have permission to view this squad.");
          setSquad(null);
        } else {
          setSquad(squadData);
          setError(null);
        }
      } else {
        setError("Squad not found.");
        setSquad(null);
      }
      setLoading(false);
    }, (err) => {
      console.error("Error fetching squad with snapshot: ", err);
      setError("Failed to load squad data.");
      setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();

  }, [currentUser, squadId, userData]);

  // Keep canonical location logistics live for completeness indicators.
  useEffect(() => {
    const locationId = String((squad as any)?.locationId || '').trim();
    if (!locationId) {
      setLocationLogistics(null);
      return;
    }
    return onSnapshot(doc(db, 'locations', locationId), (snapshot) => {
      setLocationLogistics(snapshot.exists() ? { ...snapshot.data(), id: snapshot.id } : null);
    }, (e) => {
      console.warn('Failed to load location logistics', e);
      setLocationLogistics(null);
    });
  }, [(squad as any)?.locationId]);

  // Load teachers list.
  // - Admins: load all teachers (needed for squad management).
  // - Non-admin teachers: load only the squad's teachers (reduces large queries & permissions surface area).
  useEffect(() => {
    const run = async () => {
      try {
        if (!userData) return;

        if (userData.isAdmin === true) {
          const teachersQuery = query(collection(db, 'users'), where('role', '==', 'teacher'));
          const teacherSnapshot = await getDocs(teachersQuery);
          const teacherData = teacherSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as User[];
          setTeachers(teacherData);
          return;
        }

        const teacherIds = squad?.teacherIds || [];
        if (!teacherIds.length) {
          setTeachers([]);
          return;
        }

        // Firestore "in" is limited; squads should have few teachers, but guard anyway.
        const ids = teacherIds.slice(0, 10);
        const q = query(collection(db, 'users'), where(documentId(), 'in', ids));
        const snap = await getDocs(q);
        const teacherData = snap.docs.map(doc => ({ ...doc.data(), id: doc.id })) as User[];
        setTeachers(teacherData);
      } catch (e) {
        console.warn('Failed to load teachers list', e);
      }
    };
    run();
  }, [userData, squad?.teacherIds]);

  // Allow deep-linking into a specific tab (used by teacher dashboard quick actions)
  useEffect(() => {
    const allowed = new Set(['roster', 'curriculum', 'calendar', 'portfolios']);
    if (!allowed.has(initialTabParam)) return;
    setActiveTab(initialTabParam as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTabParam, squadId]);

  // Fetch students when squad is loaded (same pattern as DashboardPane)
  useEffect(() => {
    if (!squad?.studentIds || squad.studentIds.length === 0) {
      setStudents([]);
      return;
    }

    const studentsRef = collection(db, 'users');
    const studentsQuery = query(studentsRef, where(documentId(), 'in', squad.studentIds));
    const unsubscribe = onSnapshot(studentsQuery, (studentsSnapshot) => {
      const studentsData = studentsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as User));
      setStudents(studentsData);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();
  }, [squad?.studentIds]);

  const handleSquadUpdated = (updatedSquad: Squad) => {
    setSquad(updatedSquad);
  };

  const handleRewardClick = () => {
    setIsRewardModalOpen(true);
  };

  const handleManageGamesClick = () => {
    setIsGameAccessOpen(true);
  };

  const handleRewardConfirm = async (selectedStudentData: { id: string; name: string; avatar: string }[], amount: number) => {
    try {
      // Fetch current Musecash for each selected student
      const studentsWithMusecash = await Promise.all(
        selectedStudentData.map(async (student) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', student.id));
            const userData = userDoc.data();
            const currentMusecash = userData?.museCash || 0;
            return {
              ...student,
              currentMusecash
            };
          } catch (error) {
            console.error(`Failed to fetch musecash for ${student.name}:`, error);
            return {
              ...student,
              currentMusecash: 0
            };
          }
        })
      );

      // Set up the pending reward and show confirmation modal
      setPendingReward({ students: studentsWithMusecash, amount });
      setIsRewardModalOpen(false);
      setIsRewardConfirmationOpen(true);
    } catch (error) {
      console.error('❌ Error preparing reward confirmation:', error);
      void window.appAlert('An error occurred while preparing the reward. Please try again.');
    }
  };

  const handleConfirmReward = async () => {
    if (!pendingReward) return;

    setIsAwardingReward(true);

    try {
      // Award musecash to each selected student
      const rewardResults = pendingReward.students.map(async (student) => {
        try {
          const newMuseCashAmount = student.currentMusecash + pendingReward.amount;
          await FirebaseService.updateMuseCash(student.id, newMuseCashAmount);
          console.log(`✅ Awarded ${pendingReward.amount} musecash to ${student.name} (${student.currentMusecash} → ${newMuseCashAmount})`);
          return { success: true, student: student.name };
        } catch (error) {
          console.error(`❌ Failed to award musecash to ${student.name}:`, error);
          return { success: false, student: student.name, error };
        }
      });

      // Wait for all rewards to be processed
      const results = await Promise.all(rewardResults);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        console.log(`🎉 Successfully awarded ${pendingReward.amount} musecash to ${successful.length} students!`);
      }
      if (failed.length > 0) {
        console.error(`⚠️ Failed to award musecash to ${failed.length} students:`, failed);
      }
    } catch (error) {
      console.error('❌ Error awarding musecash:', error);
      void window.appAlert('An error occurred while awarding Musecash. Please try again.');
    } finally {
      setIsAwardingReward(false);
      setIsRewardConfirmationOpen(false);
      setPendingReward(null);
    }
  };

  // Unlock Games: unlock all games up to and including the LIVE level (current session)
  const handleUnlockGames = async () => {
    if (!squad) return;
    try {
      setIsUnlocking(true);
      setUnlockMessage(null);
      const curriculumId = getSquadCurriculumId(squad) || 'music';
      const liveLevelAny = (squad as any).liveSession && (squad as any).liveSession.levelId;
      const liveLevelId = liveLevelAny ? String(liveLevelAny) : (squad.currentLessonId ? String(squad.currentLessonId) : '');
      if (!liveLevelId || squad.classStatus !== 'current') {
        setUnlockMessage('No live level found. Start the class to enable Unlock Games.');
        return;
      }
      const seasonNodes = getSeasonNodesByCurriculum(curriculumId, DEFAULT_SEASON);
      const currentLevelNum = Number(liveLevelId);
      const nodesUpToLevel = seasonNodes.filter((n: any) => {
        // Include all nodes that gate on a level <= current live level
        if (n?.requirements?.type === 'level_complete') {
          const lvl = Number(n.requirements.levelId);
          // Unlock up to ONE LESS than the current live level
          return !isNaN(lvl) && !isNaN(currentLevelNum) && lvl < currentLevelNum;
        }
        // Also include any always-unlocked/pre-level nodes (harmless if already unlocked)
        if (n?.requirements?.type === 'always_unlocked' || n?.id === 'pre_level_1') {
          return true;
        }
        return false;
      });

      const gameIds = Array.from(new Set(
        nodesUpToLevel.flatMap((n: any) =>
          (n.rewards || [])
            .filter((r: any) => r.type === 'game')
            .map((r: any) => r.gameId)
        )
      ));

      if (gameIds.length === 0) {
        setUnlockMessage('No games to unlock up to the current level.');
        return;
      }
      const studentIds = squad.studentIds || [];
      if (studentIds.length === 0) {
        setUnlockMessage('No students found on this squad.');
        return;
      }
      await SeasonPassService.addUnlockedGamesForUsers(studentIds, gameIds, { squadId: squad.id, curriculumId });
      setUnlockMessage(`Unlocked ${gameIds.length} game(s) up to Level ${liveLevelId} for ${studentIds.length} student(s).`);
    } catch (e) {
      console.error('Failed to unlock games for class', e);
      setUnlockMessage('Failed to unlock games.');
    } finally {
      setIsUnlocking(false);
      setTimeout(() => setUnlockMessage(null), 4000);
    }
  };

  // Non-music classes: classroom tools live in the left teacher rail (not the floating dock).
  useEffect(() => {
    if (loading || error || !squad || usesLegacyMusicDashboard(squad)) {
      clearTools();
      return;
    }

    setTools([
      {
        id: 'timer',
        emoji: '⏰',
        label: 'Timer',
        onClick: () => setIsTimerOpen(true),
      },
      {
        id: 'bonus',
        emoji: '🏃',
        label: 'Bonus',
        onClick: () => setIsBonusActivitiesOpen(true),
      },
      {
        id: 'bug',
        emoji: '🐛',
        label: 'Bug',
        onClick: () => window.open('https://tally.so/r/31a5MO', '_blank', 'noopener,noreferrer'),
      },
      {
        id: 'music',
        emoji: '🎵',
        label: 'Music',
        onClick: () => setIsMusicOpen(true),
      },
    ]);

    return () => clearTools();
  }, [loading, error, squad, setTools, clearTools]);

  if (loading) {
    return <LoadingSpinner fullPage label="Loading squad dashboard…" size="lg" />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!squad) {
    return <div>Squad not found.</div>;
  }

  const handleReportBug = () => {
    window.open('https://tally.so/r/31a5MO', '_blank', 'noopener,noreferrer');
  };

  const isMusicClass = usesLegacyMusicDashboard(squad);
  const squadForLogistics = { ...squad } as any;
  if (locationLogistics) {
    for (const key of LOCATION_TEACHING_LOGISTICS_FIELDS) {
      if (Object.prototype.hasOwnProperty.call(locationLogistics, key)) {
        squadForLogistics[key] = locationLogistics[key];
      }
    }
    const mergedUnknown = { ...((squad as any).teacherLogisticsUnknown || {}) };
    for (const key of LOCATION_TEACHING_LOGISTICS_FIELDS) {
      delete mergedUnknown[key];
      if (locationLogistics.teacherLogisticsUnknown?.[key] === true) mergedUnknown[key] = true;
    }
    squadForLogistics.teacherLogisticsUnknown = mergedUnknown;
    const canonicalName = String(locationLogistics.name || '').trim();
    const canonicalAddress = String(locationLogistics.address || locationLogistics.location?.address || '').trim();
    if (canonicalName) {
      squadForLogistics.locationName = canonicalName;
      squadForLogistics.schoolName = canonicalName;
    }
    if (canonicalAddress) squadForLogistics.address = canonicalAddress;
  }

  return (
    <DashboardWallpaper scope="squad" scopeId={squad.id} canEdit={Boolean(userData?.isAdmin || (currentUser && squad.teacherIds?.includes(currentUser.uid)))}><div className="squad-dashboard-page">
      {isMusicClass ? (
        <>
          <HeaderBar
            title={squad.title}
            code={squad.code}
            squad={squadForLogistics}
            onInfoClick={() => navigate(`/app/squads/${squad.id}/details`)}
            showMusicExtras={isMusicClass}
          />
          {unlockMessage && (
            <div style={{ marginTop: 8, marginLeft: 16, color: '#155724', fontSize: 14 }}>{unlockMessage}</div>
          )}
          <div className="dashboard-content">
            <DashboardPane squad={squad} initialOpenAttendance={shouldOpenAttendance} onAddStudent={() => setIsAddStudentOpen(true)} />
          </div>
        </>
      ) : (
        <>
          {unlockMessage && (
            <div style={{ marginTop: 8, marginLeft: 16, color: '#155724', fontSize: 14 }}>{unlockMessage}</div>
          )}
          <ClassroomDashboard
            squad={squadForLogistics}
            students={students}
            logisticsIncomplete={hasIncompleteTeacherLogistics(squadForLogistics)}
            onSquadDetails={() => navigate(`/app/squads/${squad.id}/details`)}
            onAddStudent={() => setIsAddStudentOpen(true)}
            initialTab={
              initialTabParam === 'portfolios'
                ? 'portfolios'
                : initialTabParam === 'curriculum' || initialTabParam === 'calendar'
                ? 'classes'
                : 'board'
            }
          />
        </>
      )}

      <RewardModal
        isOpen={isRewardModalOpen}
        onClose={() => setIsRewardModalOpen(false)}
        students={students.map(student => ({
          id: student.id,
          name: student.displayName || 'Student',
          avatar: (student.selectedMuse && typeof student.selectedMuse === 'object' && student.selectedMuse.img)
            ? student.selectedMuse.img
            : '/default-avatar.png'
        }))}
        onConfirmReward={handleRewardConfirm}
        isAwarding={false}
      />

      <RewardConfirmationModal
        isOpen={isRewardConfirmationOpen}
        onClose={() => setIsRewardConfirmationOpen(false)}
        selectedStudents={pendingReward?.students || []}
        rewardAmount={pendingReward?.amount || 0}
        onConfirm={handleConfirmReward}
        isAwarding={isAwardingReward}
      />

      {isMusicClass ? (
        <TeacherToolbelt
          onRewardClick={handleRewardClick}
          onManageGamesClick={handleManageGamesClick}
          onReportBugClick={handleReportBug}
          onTimerClick={() => setIsTimerOpen(true)}
          onBonusActivitiesClick={() => setIsBonusActivitiesOpen(true)}
          onMusicClick={() => setIsMusicOpen(true)}
          onBoardModeClick={() => setIsBoardModeOpen(true)}
          isUnlocking={isUnlocking}
        />
      ) : (
        <div className="teacher-toolbelt-mobile-fallback">
          <TeacherToolbelt
            onReportBugClick={handleReportBug}
            onTimerClick={() => setIsTimerOpen(true)}
            onBonusActivitiesClick={() => setIsBonusActivitiesOpen(true)}
            onMusicClick={() => setIsMusicOpen(true)}
          />
        </div>
      )}

      <SquadGameAccessModal
        squad={squad}
        isOpen={isGameAccessOpen}
        onClose={() => setIsGameAccessOpen(false)}
        teacherId={currentUser?.uid}
        teacherName={currentUser?.displayName || 'Teacher'}
      />

      <SquadBoardModeModal
        squad={squad}
        students={students}
        isOpen={isBoardModeOpen}
        onClose={() => setIsBoardModeOpen(false)}
      />

      {isTimerOpen && (
        <DraggableTimer onClose={() => setIsTimerOpen(false)} />
      )}
      {isMusicOpen && (
        <DraggableMusic onClose={() => setIsMusicOpen(false)} />
      )}

      <BonusActivitiesModal
        isOpen={isBonusActivitiesOpen}
        onClose={() => setIsBonusActivitiesOpen(false)}
      />
      <AddStudentModal isOpen={isAddStudentOpen} squadId={squad.id} onClose={() => setIsAddStudentOpen(false)} />
    </div></DashboardWallpaper>
  );
};

export default SquadDashboard;
